import React, { useState, useMemo, useCallback, useRef } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useMutation } from "@apollo/client";

import { Card, CardContent } from "@/components/ui/card";
import { Segmented } from "@/components/ui/segmented";
import { DataTable } from "@/components/ui/data-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

import TableToolbar from "../../../common/TableToolbar";
import {
  ValueCell,
  IsBuyCellRender,
  ConcludeButton,
  CancelButton,
} from "../../../common/CustomCell";
import { CANCEL_ORDER, CONCLUDE_ORDER } from "../../../graphql/manageOrder";
import { useUserState } from "../../../../context";
import { useUserPnL } from "@/context/DataManagerContext";
import { getItemInfo } from "../../../cache/itemData";
import { sortWithActivePriority } from "@/lib/sortActiveUsers";

// ============================================================
// Types
// ============================================================

interface UserData {
  id: string;
  userName: string;
  recommendCode: string;
  liveProfitNLoss: number;
  pureProfitNLoss: number;
  totValuationGNL: number;
  totalFee: number;
  liveUserBalance: number;
  authority: number;
}

interface PositionCountData {
  gubun: string;
  domestic: string;
  callOption: string;
  putOption: string;
  foreign: string;
}

interface SignedPositionData {
  id: string;
  userName: string;
  itemCode: string;
  isBuy: boolean;
  count: number;
  price: number;
  currentPrice: number;
  valuationGNL: number;
  orderNo: string;
  userId?: string;
}

interface UnsignedPositionData {
  id: string;
  userName: string;
  itemCode: string;
  type: string;
  isBuy: boolean;
  count: number;
  price: number;
  orderNo: string;
}

// ============================================================
// Helpers
// ============================================================

const AUTHORITY_LABELS: Record<number, string> = {
  0: "준회원",
  1: "정회원",
  4: "모의투자",
};

// ============================================================
// ProfitStatus (DataTable - pagination, sorting, row selection)
// ============================================================

interface ProfitStatusProps {
  data: UserData[];
  sumData: {
    liveProfitNLoss: number;
    totValuationGNL: number;
    profitNLoss: number;
    totalFee: number;
    liveUserBalance: number;
  };
  selectedRowKeys: string[];
  handleSelectionChanged: (
    rowKeys: string[],
    selectedRows: UserData[],
  ) => void;
}

const ProfitStatus = React.memo(function ProfitStatus({
  data,
  sumData: externalSumData,
  selectedRowKeys,
  handleSelectionChanged,
}: ProfitStatusProps) {
  const PAGE_SIZE = 30;
  const [currentFilter, setCurrentFilter] = useState<number[]>([0, 1]);
  const [segmentedValue, setSegmentedValue] = useState("일반회원");
  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>([{ id: "totalFee", desc: true }]);

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
    setVisibleCount(PAGE_SIZE);
  }, []);

  // Refs: stale closure 방지 (실시간 데이터 업데이트 중 최신 값 접근)
  const selectedRowKeysRef = useRef(selectedRowKeys);
  selectedRowKeysRef.current = selectedRowKeys;
  const filteredDataRef = useRef<UserData[]>([]);

  // 필터 + 검색 적용된 데이터
  const filteredData = useMemo(() => {
    let result = data.filter((item) => currentFilter.includes(item.authority));
    if (searchQuery) {
      result = result.filter(
        (item) =>
          item.id.includes(searchQuery) ||
          item.userName.includes(searchQuery) ||
          item.recommendCode.includes(searchQuery),
      );
    }
    return result;
  }, [data, currentFilter, searchQuery]);

  // 활성 사용자 우선 + 실시간 컬럼 정렬 후 slice
  const displayData = useMemo(() => {
    const sorted = sortWithActivePriority(
      filteredData,
      sorting[0]?.id,
      sorting[0]?.desc,
    );
    return sorted.slice(0, visibleCount);
  }, [filteredData, visibleCount, sorting]);

  filteredDataRef.current = filteredData;

  const hasMore = visibleCount < filteredData.length;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  // 체크박스 선택 상태를 tanstack rowSelection 형태로 변환
  const rowSelection = useMemo(() => {
    const sel: Record<string, boolean> = {};
    selectedRowKeys.forEach((key) => {
      sel[key] = true;
    });
    return sel;
  }, [selectedRowKeys]);

  const handleCheckboxChange = useCallback(
    (id: string, checked: boolean) => {
      const current = selectedRowKeysRef.current;
      const next = checked
        ? [...current, id]
        : current.filter((k) => k !== id);
      handleSelectionChanged(next, []);
    },
    [handleSelectionChanged],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allKeys = filteredDataRef.current.map((d) => d.id);
        handleSelectionChanged(allKeys, []);
      } else {
        handleSelectionChanged([], []);
      }
    },
    [handleSelectionChanged],
  );

  // 전체 선택 상태
  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((d) => selectedRowKeys.includes(d.id));
  const isSomeSelected =
    filteredData.some((d) => selectedRowKeys.includes(d.id)) && !isAllSelected;

  // tanstack ColumnDef
  const columns = useMemo<ColumnDef<UserData>[]>(
    () => [
      {
        id: "select",
        size: 40,
        enableSorting: false,
        header: () => (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
              onCheckedChange={(checked) => handleSelectAll(!!checked)}
              aria-label="전체 선택"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={rowSelection[row.original.id] ?? false}
              onCheckedChange={(checked) =>
                handleCheckboxChange(row.original.id, !!checked)
              }
              aria-label={`${row.original.id} 선택`}
            />
          </div>
        ),
      },
      {
        accessorKey: "id",
        header: "아이디",
        size: 100,
        enableSorting: true,
      },
      {
        accessorKey: "userName",
        header: "이름",
        size: 80,
        enableSorting: true,
      },
      {
        accessorKey: "recommendCode",
        header: "추천인",
        size: 80,
        enableSorting: true,
      },
      {
        accessorKey: "liveProfitNLoss",
        header: "실시간손익",
        size: 96,
        enableSorting: true,
        cell: ({ getValue }) => <ValueCell value={getValue() as number} />,
      },
      {
        accessorKey: "pureProfitNLoss",
        header: "실현손익",
        size: 96,
        enableSorting: true,
        cell: ({ getValue }) => <ValueCell value={getValue() as number} />,
      },
      {
        accessorKey: "totValuationGNL",
        header: "평가손익",
        size: 96,
        enableSorting: true,
        cell: ({ getValue }) => <ValueCell value={getValue() as number} />,
      },
      {
        accessorKey: "totalFee",
        header: "수수료",
        size: 80,
        enableSorting: true,
        cell: ({ getValue }) => <ValueCell value={getValue() as number} />,
      },
      {
        accessorKey: "liveUserBalance",
        header: "평가담보금",
        size: 96,
        enableSorting: true,
        cell: ({ getValue }) => (
          <ValueCell value={getValue() as number} type={0} />
        ),
      },
      {
        accessorKey: "authority",
        header: "등급",
        size: 80,
        enableSorting: false,
        cell: ({ getValue }) =>
          AUTHORITY_LABELS[getValue() as number] ?? getValue(),
      },
    ],
    [rowSelection, isAllSelected, isSomeSelected, handleCheckboxChange, handleSelectAll],
  );

  // summaryData for DataTable (cell 렌더러 활용)
  const summaryData = useMemo(
    () =>
      ({
        liveProfitNLoss: externalSumData.liveProfitNLoss,
        pureProfitNLoss: externalSumData.profitNLoss,
        totValuationGNL: externalSumData.totValuationGNL,
        totalFee: externalSumData.totalFee,
        liveUserBalance: externalSumData.liveUserBalance,
      }) as unknown as Partial<UserData>,
    [externalSumData],
  );

  const handleSegmentedChanged = useCallback((value: string) => {
    setSegmentedValue(value);
    if (value === "전체") setCurrentFilter([0, 1, 4]);
    if (value === "일반회원") setCurrentFilter([0, 1]);
    if (value === "테스터") setCurrentFilter([4]);
  }, []);

  const handleOnSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setSearchValue("");
      setVisibleCount(PAGE_SIZE);
    },
    [],
  );

  const handleReload = useCallback(() => {
    setSearchQuery("");
    setSearchValue("");
    setSegmentedValue("일반회원");
    setCurrentFilter([0, 1]);
    setVisibleCount(PAGE_SIZE);
  }, []);

  return (
    <Card className="h-full flex flex-col">
      <CardContent className="flex-1 flex flex-col overflow-hidden p-3">
        <TableToolbar
          left={
            <Segmented
              options={["전체", "일반회원", "테스터"]}
              onChange={handleSegmentedChanged}
              variant="solid"
              size="default"
              value={segmentedValue}
            />
          }
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSearch={handleOnSearch}
          onReset={handleReload}
        />

        {/* DataTable */}
        <div className="flex-1 min-h-0">
          <DataTable
            columns={columns}
            data={displayData}
            getRowId={(row) => row.id}
            stickyHeader
            infiniteScroll
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            manualSorting
            initialSorting={sorting}
            onSortingChange={handleSortingChange}
            summaryData={summaryData as UserData}
            summaryPosition="top"
            summaryLabel={{ text: '합계', colSpan: 4 }}
            summaryClassName="bg-amber-50 font-semibold"
          />
        </div>
      </CardContent>
    </Card>
  );
});

// ============================================================
// CurrentPositionStatus (simple shadcn Table)
// ============================================================

const CurrentPositionStatus = React.memo(function CurrentPositionStatus({
  data,
}: {
  data: PositionCountData[];
}) {
  return (
    <div className="shrink-0">
      <h3 className="font-bold text-sm mb-1">보유포지션</h3>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-20">구분</TableHead>
            <TableHead className="w-24">선물(L/S)</TableHead>
            <TableHead className="w-24">콜옵션(L/S)</TableHead>
            <TableHead className="w-24">풋옵션(L/S)</TableHead>
            <TableHead className="w-24">해외선물(L/S)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((row) => (
              <TableRow key={row.gubun}>
                <TableCell>{row.gubun}</TableCell>
                <TableCell>{row.domestic}</TableCell>
                <TableCell>{row.callOption}</TableCell>
                <TableCell>{row.putOption}</TableCell>
                <TableCell>{row.foreign}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center text-muted-foreground py-4"
              >
                검색 결과가 없습니다
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
});

// ============================================================
// MemberPosition (simple shadcn Table + conclude mutation)
// ============================================================

const MemberPosition = React.memo(function MemberPosition({
  data,
}: {
  data: SignedPositionData[];
}) {
  const [concludeOrder] = useMutation(CONCLUDE_ORDER);
  const {
    user: { userId: managerId },
  } = useUserState();
  const containerRef = useRef<HTMLDivElement>(null);
  const colMaxWidths = useRef<Record<string, number>>({});

  // 열 너비 래칫: 현재가/평가손익 컬럼이 한번 넓어지면 줄어들지 않음
  React.useLayoutEffect(() => {
    if (!containerRef.current) return;
    ['currentPrice', 'valuationGNL'].forEach((colKey) => {
      const cells = containerRef.current!.querySelectorAll<HTMLElement>(`[data-col="${colKey}"]`);
      cells.forEach((cell) => {
        const width = cell.offsetWidth;
        const prev = colMaxWidths.current[colKey] || 0;
        const max = Math.max(prev, width);
        colMaxWidths.current[colKey] = max;
        cell.style.minWidth = `${max}px`;
      });
    });
  });

  return (
    <div ref={containerRef} className="flex flex-col flex-1 min-h-0">
      <h3 className="font-bold text-sm mb-1 shrink-0">회원별 포지션</h3>
      <div className="flex-1 min-h-0 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[72px]">아이디</TableHead>
              <TableHead className="w-20">이름</TableHead>
              <TableHead className="w-14">종목</TableHead>
              <TableHead className="w-10">구분</TableHead>
              <TableHead className="w-10">수량</TableHead>
              <TableHead className="w-16">평균가</TableHead>
              <TableHead data-col="currentPrice" className="w-16">현재가</TableHead>
              <TableHead data-col="valuationGNL" className="w-20">평가손익</TableHead>
              <TableHead className="w-16">청산</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((row) => (
                <TableRow key={row.orderNo}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell>{row.itemCode}</TableCell>
                  <TableCell>
                    <IsBuyCellRender value={row.isBuy} />
                  </TableCell>
                  <TableCell>{row.count}</TableCell>
                  <TableCell>{row.price}</TableCell>
                  <TableCell data-col="currentPrice">{row.currentPrice}</TableCell>
                  <TableCell data-col="valuationGNL">
                    <ValueCell value={row.valuationGNL} />
                  </TableCell>
                  <TableCell className="p-0">
                    <ConcludeButton
                      managerId={managerId}
                      concludeOrder={concludeOrder}
                      data={row}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground py-4"
                >
                  검색 결과가 없습니다
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});

// ============================================================
// UnsignedPositon (simple shadcn Table + cancel mutation)
// ============================================================

const UnsignedPositon = React.memo(function UnsignedPositon({
  data,
}: {
  data: UnsignedPositionData[];
}) {
  const [cancelOrder] = useMutation(CANCEL_ORDER);
  const {
    user: { userId: managerId },
  } = useUserState();

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <h3 className="font-bold text-sm mb-1 shrink-0">미체결 포지션</h3>
      <div className="flex-1 min-h-0 overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-20">아이디</TableHead>
              <TableHead className="w-20">이름</TableHead>
              <TableHead className="w-14">종목</TableHead>
              <TableHead className="w-14">미체결</TableHead>
              <TableHead className="w-10">구분</TableHead>
              <TableHead className="w-10">수량</TableHead>
              <TableHead className="w-16">주문가격</TableHead>
              <TableHead className="w-16">취소</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((row) => (
                <TableRow key={row.orderNo}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell>{row.itemCode}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    <IsBuyCellRender value={row.isBuy} />
                  </TableCell>
                  <TableCell>{row.count}</TableCell>
                  <TableCell>{row.price}</TableCell>
                  <TableCell className="p-0">
                    <CancelButton
                      managerId={managerId}
                      cancelOrder={cancelOrder}
                      data={row}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-muted-foreground py-4"
                >
                  검색 결과가 없습니다
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});

// ============================================================
// OrderStatus (Main Container)
// ============================================================

const OrderStatus = () => {
  const { userPnLData, sumData, isDataReady } = useUserPnL();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // 보유포지션 집계 (Users.initializeOwnPosition 대체)
  const positionCountData = useMemo<PositionCountData[]>(() => {
    const groups = [
      { gubun: "정회원", authorities: [0, 1], domestic: { buy: 0, sell: 0 }, callOption: { buy: 0, sell: 0 }, putOption: { buy: 0, sell: 0 }, foreign: { buy: 0, sell: 0 } },
      { gubun: "테스터", authorities: [4], domestic: { buy: 0, sell: 0 }, callOption: { buy: 0, sell: 0 }, putOption: { buy: 0, sell: 0 }, foreign: { buy: 0, sell: 0 } },
    ];

    userPnLData.forEach((user: any) => {
      const group = groups.find((g) => g.authorities.includes(user.authority));
      if (!group || !user.position) return;
      user.position.forEach((pos: any) => {
        if (!pos.isSigned) return;
        const itemInfo = getItemInfo(pos.itemCode);
        if (!itemInfo) return;
        const category =
          itemInfo.type === 0 ? "domestic" :
          itemInfo.type === 1 ? "callOption" :
          itemInfo.type === 2 ? "putOption" : "foreign";
        (group as any)[category][pos.isBuy ? "buy" : "sell"] += pos.count;
      });
    });

    const formatted = groups.map((g) => ({
      gubun: g.gubun,
      domestic: `${g.domestic.buy} / ${g.domestic.sell}`,
      callOption: `${g.callOption.buy} / ${g.callOption.sell}`,
      putOption: `${g.putOption.buy} / ${g.putOption.sell}`,
      foreign: `${g.foreign.buy} / ${g.foreign.sell}`,
    }));

    formatted.push({
      gubun: "합계",
      domestic: `${groups.reduce((s, g) => s + g.domestic.buy, 0)} / ${groups.reduce((s, g) => s + g.domestic.sell, 0)}`,
      callOption: `${groups.reduce((s, g) => s + g.callOption.buy, 0)} / ${groups.reduce((s, g) => s + g.callOption.sell, 0)}`,
      putOption: `${groups.reduce((s, g) => s + g.putOption.buy, 0)} / ${groups.reduce((s, g) => s + g.putOption.sell, 0)}`,
      foreign: `${groups.reduce((s, g) => s + g.foreign.buy, 0)} / ${groups.reduce((s, g) => s + g.foreign.sell, 0)}`,
    });

    return formatted;
  }, [userPnLData]);

  // 선택된 사용자의 체결/미체결 포지션 (userPnLData 변경 시 자동 재계산)
  const { signedPositionCountData, unsignedPositionCountData } = useMemo(() => {
    if (selectedRowKeys.length === 0) {
      return { signedPositionCountData: [] as SignedPositionData[], unsignedPositionCountData: [] as UnsignedPositionData[] };
    }

    const selectedUsers = userPnLData.filter((u: any) => selectedRowKeys.includes(u.id));
    const signed: SignedPositionData[] = [];
    const unsigned: UnsignedPositionData[] = [];
    selectedUsers.forEach((user: any) => {
      user.position?.forEach((pos: any) => {
        if (pos.isSigned) signed.push(pos);
        else if (pos.type < 2) unsigned.push(pos);
      });
    });
    return { signedPositionCountData: signed, unsignedPositionCountData: unsigned };
  }, [userPnLData, selectedRowKeys]);

  // 회원 선택 핸들러
  const handleSelectionChanged = useCallback(
    (rowkeys: string[], _selectedRows: UserData[]) => {
      setSelectedRowKeys(rowkeys);
    },
    [],
  );

  if (!isDataReady) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        데이터 로딩 중...
      </div>
    );
  }

  return (
    <div className="flex h-full gap-4">
      <div className="w-[55%] min-w-[400px] overflow-auto">
        <ProfitStatus
          data={userPnLData}
          sumData={sumData}
          selectedRowKeys={selectedRowKeys}
          handleSelectionChanged={handleSelectionChanged}
        />
      </div>
      <div className="flex flex-1 flex-col gap-3 min-h-0">
        <CurrentPositionStatus data={positionCountData} />
        <MemberPosition data={signedPositionCountData} />
        <UnsignedPositon data={unsignedPositionCountData} />
      </div>
    </div>
  );
};

export default React.memo(OrderStatus);
